// ──────────────────────────────────────────────────────────────────────────────
// Copyright (c) Cognultsoft. All rights reserved.
// Product: Camptain · Module: Sign-in
//
// The school sign-in: Azure AD B2C with MFA, the same flow as DebtCollectify
// (debtcollectify-ui/src/auth). A popup shows B2C's own page (email, password, then
// the MFA step the policy asks for) and always asks again (prompt=login). Tokens are
// renewed silently from MSAL's cache against the policy the account signed in with,
// without a hidden iframe, so third-party-cookie blocking does not break it.
//
// Settings (.env): VITE_B2C_CLIENT_ID, VITE_B2C_INSTANCE, VITE_B2C_DOMAIN,
// VITE_B2C_POLICY, VITE_B2C_SCOPES, VITE_B2C_USE_ACCESS_TOKEN.
// ──────────────────────────────────────────────────────────────────────────────

import {
  CacheLookupPolicy,
  EventType,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type SilentRequest,
} from '@azure/msal-browser';

import { hasSignedOut, sessionGeneration, sessionIsCurrent } from './sessionLifecycle';

const env = import.meta.env;
const instance = (env.VITE_B2C_INSTANCE ?? '').replace(/\/+$/, '');
const domain = env.VITE_B2C_DOMAIN ?? '';
const policy = env.VITE_B2C_POLICY || 'B2C_1A_SIGNIN';

/** True when the B2C settings are filled in; otherwise the app runs without sign-in. */
export const b2cConfigured = Boolean(env.VITE_B2C_CLIENT_ID && instance && domain);

const authority = `${instance}/${domain}/${policy}`;
const scopes = ['openid', 'profile', ...(env.VITE_B2C_SCOPES ? [env.VITE_B2C_SCOPES] : [])];
// DebtCollectify sends the ID token unless told to send the access token for its API scope.
const useAccessToken = env.VITE_B2C_USE_ACCESS_TOKEN === 'true';

let client: PublicClientApplication | null = null;
let ready: Promise<PublicClientApplication> | null = null;

/** The one MSAL client, created and initialized on first use. */
function msal(): Promise<PublicClientApplication> {
  if (!b2cConfigured) return Promise.reject(new Error("School sign-in isn't connected yet."));
  ready ??= (async () => {
    client = new PublicClientApplication({
      auth: {
        clientId: env.VITE_B2C_CLIENT_ID!,
        authority,
        knownAuthorities: [instance.replace('https://', '').split('/')[0]!],
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      // Someone typing their password and MFA code can take longer than MSAL's 60 s popup watchdog.
      system: { iframeBridgeTimeout: 10_000, popupBridgeTimeout: 300_000 },
    });
    await client.initialize();
    client.addEventCallback((event) => {
      const payload = event.payload as AuthenticationResult | null;
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        payload?.account
      )
        client!.setActiveAccount(payload.account);
    });
    if (!client.getActiveAccount() && client.getAllAccounts()[0])
      client.setActiveAccount(client.getAllAccounts()[0]!);
    return client;
  })();
  return ready;
}

const tokenOf = (result: AuthenticationResult) =>
  useAccessToken ? result.accessToken : result.idToken;

/** Silent renewal for a signed-in account: the policy it signed in with (its tfp claim), cache and refresh token only. */
function silentRequestFor(account: AccountInfo): SilentRequest {
  const claims = account.idTokenClaims;
  const signedInWith = typeof claims?.tfp === 'string' ? claims.tfp : policy;
  return {
    scopes,
    account,
    authority: `${instance}/${domain}/${signedInWith}`,
    cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
  };
}

/**
 * MSAL marks a sign-in as in progress in sessionStorage while its popup is open. A popup that was
 * closed or failed can leave the mark behind, and every later Sign in then fails with
 * interaction_in_progress. The sign-in page allows one attempt at a time, so a mark found at the
 * start of a new attempt is always stale.
 */
function clearStaleInteraction(): void {
  try {
    for (const key of Object.keys(sessionStorage))
      if (key.endsWith('interaction.status')) sessionStorage.removeItem(key);
  } catch {
    // Storage blocked: nothing was kept either.
  }
}

/** Opens B2C's sign-in (email, password, MFA) in a popup and returns the token to send to Camptain. */
export async function getB2CAccessToken(): Promise<string> {
  const app = await msal();
  clearStaleInteraction();
  const result = await app.loginPopup({ scopes, prompt: 'login' });
  app.setActiveAccount(result.account);
  return tokenOf(result);
}

/** A current token for the signed-in account without asking again; null when there is no account or it has expired. */
export async function renewB2CAccessToken(): Promise<string | null> {
  if (!b2cConfigured || hasSignedOut()) return null;
  const attempt = sessionGeneration();
  try {
    const app = await msal();
    const account = app.getActiveAccount();
    const result = account
      ? tokenOf(await app.acquireTokenSilent(silentRequestFor(account)))
      : null;
    return sessionIsCurrent(attempt) ? result : null;
  } catch {
    return null;
  }
}

/** Ends the B2C session too, on sign-out. */
export async function signOutOfB2C(): Promise<void> {
  const destination = new URL('/signin', window.location.origin).href;
  if (b2cConfigured) {
    const app = await msal();
    const account = app.getActiveAccount();
    try {
      await app.logoutRedirect({ account, postLogoutRedirectUri: destination });
      return;
    } catch {
      // A failed navigation must still discard the local identity and tokens.
      await app.clearCache().catch(() => undefined);
      app.setActiveAccount(null);
    }
  }
  window.location.replace(destination);
}
